import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import EveryDoggyAPIServices from '../../../../../utils/services/EveryDoggyAPIServices';
import { getParameterFromURL } from '../../../../../utils/scripts';
import { getRegionalCurrencyPrefix, getUsdCurrencyPrefix } from '../../../../../utils/scripts/currency';
import { determinePaymentVersion } from '../../../../../utils/scripts/paymentVersion';
import {
  buildInternalPDFId,
  getPDFOfferId,
  K9_PRODUCT_KEY,
  PDFOfferCurrency,
} from '../../../../../utils/scripts/getInternalPDFId';
import { WebCatalogDigitalProductOferList } from '../../../../../utils/services/EveryDoggyAPITypes';
import { setDigitalProduct } from '../../../../../reduxStore/data/actions';
import { analyticsEventsTracker, EVENT_TRACKERS, GTMOnboardingFunnelEvent } from '../../../../../utils/analytics';
import { isPDFHighPricesGroupB } from '../../../../../utils/scripts/pdfHighPricesTest';

/** Price of the PDF offer: 29.99 on the p29 paywall, 19.99 everywhere else. */
const PDF_PRICE_DEFAULT = '19.99';
const PDF_PRICE_P29 = '29.99';
/** Group B of the `p29v74_PDF_highPrices29_39` test — p29 only. */
const PDF_PRICE_P29_HIGH = '39.99';
/** The K9 guide exists at one price only. */
const PDF_PRICE_K9 = '19.99';

export type DigitalProductPriceOptions = {
  /**
   * `true` for the K9 Reactivity Field Manual: a single offer per currency at 19.99,
   * not split by `?role` and not affected by the p29 price test.
   */
  k9Guide?: boolean;
};

export type DigitalProductPrice = {
  price: string;
  oldPrice: string;
  currency: string;
};

function addEventsShowPDFUpsele(role: string, contentIdsPdf: string[]) {
  analyticsEventsTracker.trackWithTarget(GTMOnboardingFunnelEvent.showPdfUpsale(role, contentIdsPdf), EVENT_TRACKERS.GTM);
}

/**
 * Loads the PDF upsell offer, puts it into the store and fires `show_pdf_upsale`.
 *
 * Shared by every PDF upsell design so that the product resolution, the redux
 * dispatch and the analytics event stay identical across AB variants — only the
 * markup around the returned values differs. Mount exactly one consumer per screen,
 * otherwise `show_pdf_upsale` is tracked twice.
 */
export const useDigitalProductPrice = ({ k9Guide = false }: DigitalProductPriceOptions = {}): DigitalProductPrice => {
  const dispatch = useDispatch();
  const [digitalProductList, setDigitalProductList] = useState<WebCatalogDigitalProductOferList>();
  const [digitalProductName, setDigitalProductName] = useState<string>('adult_dog_guide');
  const [price, setPrice] = useState<string>('');
  const [oldPrice, setOldPrice] = useState<string>('');
  const role = getParameterFromURL('role');
  const [indexShowEvent, setIndexShowEvent] = useState(true);
  const subscrUK = getParameterFromURL('subscrUK') === 'true';
  const subscrEU = getParameterFromURL('subscrEU') === 'true';
  const currency = getRegionalCurrencyPrefix();

  const isP29 = determinePaymentVersion() === 'p29';
  const targetPrice = k9Guide
    ? PDF_PRICE_K9
    : isP29
      ? isPDFHighPricesGroupB()
        ? PDF_PRICE_P29_HIGH
        : PDF_PRICE_P29
      : PDF_PRICE_DEFAULT;

  const region: PDFOfferCurrency = subscrUK ? 'GBP' : subscrEU ? 'EUR' : 'USD';

  useEffect(() => {
    const contentIdsPdf = k9Guide
      ? [buildInternalPDFId(K9_PRODUCT_KEY, targetPrice, region)]
      : [
          buildInternalPDFId('puppy_guide', targetPrice, region),
          buildInternalPDFId('adult_dog_guide', targetPrice, region),
        ];

    if (price && indexShowEvent) {
      const eventRole = k9Guide ? 'k9' : role === 'puppy' ? 'puppy' : 'adult';

      addEventsShowPDFUpsele(`${eventRole}_${price}`, contentIdsPdf);
      setIndexShowEvent(false);
    }
  }, [price, role, region, targetPrice, k9Guide]);

  useEffect(() => {
    async function fetchData() {
      const data = await EveryDoggyAPIServices.getWebCatalogDigitalProductOferList();
      if (!data) {
        return;
      }
      setDigitalProductList(data);
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (k9Guide) {
      setDigitalProductName(K9_PRODUCT_KEY);
    } else if (role === 'puppy') {
      setDigitalProductName('puppy_guide');
    } else {
      setDigitalProductName('adult_dog_guide');
    }
  }, [role, k9Guide]);

  useEffect(() => {
    if (!digitalProductList?.length) return;

    const offerId = getPDFOfferId(digitalProductName, targetPrice, region);
    const product = digitalProductList.find((item) => item.id === offerId);

    if (!product) {
      console.warn('[DigitalProduct] Product not found', {
        digitalProductName,
        region,
        targetPrice,
      });
      return;
    }

    dispatch(setDigitalProduct(product));
    setPrice(product.price);
  }, [digitalProductList, digitalProductName, region, targetPrice]);

  useEffect(() => {
    if (price) {
      const oldPriceValue = ((100 * Number(price)) / 46).toFixed(2);
      setOldPrice(oldPriceValue);
    }
  }, [price]);

  return { price, oldPrice, currency };
};

/**
 * Same contract as `useDigitalProductPrice`, for the `?ppPDFV2` PayPal flow where the
 * offer is the fixed 0.99 one and the old price is not derived from it.
 */
export const useDigitalProductPriceV2 = (): DigitalProductPrice => {
  const dispatch = useDispatch();
  const [digitalProductList, setDigitalProductList] = useState<WebCatalogDigitalProductOferList>();
  const [digitalProductName, setDigitalProductName] = useState<string>('adult_dog_guide');
  const [price, setPrice] = useState<string>('');
  const [oldPrice, setOldPrice] = useState<string>('');
  const role = getParameterFromURL('role');
  const [indexShowEvent, setIndexShowEvent] = useState(true);
  const currency = getUsdCurrencyPrefix();

  useEffect(() => {
    const contentIdsPdf = ['everydoggy_PDF_puppy_00_99_USD', 'everydoggy_PDF_adult_00_99_USD'];

    if (price && indexShowEvent) {
      if (role === 'puppy') {
        addEventsShowPDFUpsele(`puppy_${price}`, contentIdsPdf);
      } else {
        addEventsShowPDFUpsele(`adult_${price}`, contentIdsPdf);
      }
      setIndexShowEvent(false);
    }
  }, [price, role]);

  useEffect(() => {
    async function fetchData() {
      const data = await EveryDoggyAPIServices.getWebCatalogDigitalProductOferList();
      if (!data) {
        return;
      }
      setDigitalProductList(data);
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (role === 'puppy') {
      setDigitalProductName('puppy_guide');
    } else {
      setDigitalProductName('adult_dog_guide');
    }
  }, [role]);

  useEffect(() => {
    if (digitalProductList) {
      const product = digitalProductList.find((item) => {
        return item.product_key === digitalProductName && item.price === '0.99';
      });
      dispatch(setDigitalProduct(product));
      setPrice(product?.price ?? price);
    }
  }, [digitalProductList, digitalProductName]);

  useEffect(() => {
    setOldPrice('43.46');
  }, [price]);

  return { price, oldPrice, currency };
};
